import type { ProjectSummary } from "@dramapie/shared";
import { useEffect, useRef } from "react";

export interface ProjectSwitcherProps {
  open: boolean;
  currentProjectId: string;
  projects: readonly ProjectSummary[];
  loading?: boolean;
  onClose: () => void;
  onSwitch: (projectId: string) => void;
  /** 回项目列表页（`/`）。 */
  onOpenAll: () => void;
  onNewProject: () => void;
}

/**
 * 左上角作品切换（Wiki G-01）。
 *
 * 原型里这一块是「仙大 ⌄」：点开列全部作品 + 新增。
 * 我们的多作品就是多个 project，所以直接列 `GET /api/projects`。
 * 底部固定「全部项目」——用户必须随时能回到列表页，这是导航的兜底。
 */
export function ProjectSwitcher({
  open,
  currentProjectId,
  projects,
  loading = false,
  onClose,
  onSwitch,
  onOpenAll,
  onNewProject,
}: ProjectSwitcherProps) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) onClose();
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="dp-project-menu" role="menu" aria-label="切换作品" ref={ref}>
      <header className="dp-project-menu-head">
        <span>作品</span>
        <span className="dp-tiny-note">{projects.length}</span>
      </header>

      {loading && projects.length === 0 ? (
        <p className="dp-project-empty">读取中…</p>
      ) : projects.length === 0 ? (
        <p className="dp-project-empty">还没有作品</p>
      ) : (
        <ul className="dp-project-list">
          {projects.map((project) => {
            const current = project.id === currentProjectId;
            return (
              <li key={project.id}>
                <button
                  type="button"
                  className="dp-project-item"
                  role="menuitemradio"
                  aria-checked={current}
                  onClick={() => onSwitch(project.id)}
                >
                  <span className="dp-project-mark" aria-hidden="true">
                    {project.title.slice(0, 1) || "·"}
                  </span>
                  <span className="dp-project-text">
                    <b>{project.title}</b>
                    <em>
                      {project.episodeCount} 集 · {project.shotCount} 镜
                    </em>
                  </span>
                  {current ? <span className="dp-project-current">当前</span> : null}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <footer className="dp-project-menu-foot">
        <button type="button" className="dp-project-action" onClick={onNewProject}>
          ＋ 新建作品
        </button>
        <button type="button" className="dp-project-action" onClick={onOpenAll}>
          全部作品 ›
        </button>
      </footer>
    </div>
  );
}
